/** @jsx jsx */
import { jsx } from "@emotion/core"
import React, { Fragment } from "react"
import { Link } from "gatsby"
import { PropsOf } from "../../utils/types"
import { ThemeCss } from "../../theme"
import { sizeStyles } from "../Text/Text.helpers"

type BreadcrumbVariantProps = {
  separator?: `arrow` | `slash`
  size?: "L" | "M" | "S"
}

export type BreadcrumbProps = BreadcrumbVariantProps &
  Omit<JSX.IntrinsicElements["nav"], "ref">

export function Breadcrumb({
  children,
  separator = `arrow`,
  size = `L`,
  ...rest
}: BreadcrumbProps) {
  return (
    <nav aria-label="breadcrumb" {...rest}>
      <ol
        css={{
          listStyle: `none`,
          margin: 0,
          padding: 0,
          display: `flex`,
          flexWrap: `wrap`,
        }}
      >
        {React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { separator, size })
          }
          return child
        })}
      </ol>
    </nav>
  )
}

Breadcrumb.Item = BreadcrumbItem

const breadcrumbItemCss: ThemeCss = theme => ({
  display: `flex`,
  alignItems: `center`,
  color: theme.colors.purple[50],
  fontFamily: theme.fonts.heading,
})

export type BreadcrumbItemProps = PropsOf<"li"> &
  BreadcrumbVariantProps & {
    to?: string
    active?: boolean
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
  }

function BreadcrumbItem({
  to,
  active,
  separator,
  size = `L`,
  onClick,
  children,
  ...rest
}: BreadcrumbItemProps) {
  return (
    <li
      css={theme => [
        breadcrumbItemCss(theme),
        sizeStyles[size](theme),
        active && {
          color: theme.colors.grey[60],
        },
      ]}
      {...rest}
    >
      {to ? (
        <Fragment>
          <Link
            to={to}
            onClick={onClick}
            css={{
              textDecoration: `none`,
              color: `inherit`,
            }}
          >
            {children}
          </Link>
          <BreadcrumbSeparator separator={separator} />
        </Fragment>
      ) : (
        children
      )}
    </li>
  )
}

BreadcrumbItem.displayName = `Breadcrumb.Item`

const baseSeparatorCss: ThemeCss = theme => ({
  color: theme.colors.grey[60],
  marginRight: theme.space[4],
  marginLeft: theme.space[4],
  verticalAlign: `middle`,
})

const breadcrumbSlashCss: ThemeCss = theme => [
  baseSeparatorCss(theme),
  {
    color: theme.colors.grey[30],
  },
]

const breadcrumbArrowCss: ThemeCss = theme => [
  baseSeparatorCss(theme),
  {
    color: theme.colors.grey[60],
  },
]

function BreadcrumbSeparator({
  separator,
  ...rest
}: BreadcrumbVariantProps & JSX.IntrinsicElements["svg"]) {
  return separator === `arrow` ? (
    <svg
      width="6"
      height="9"
      viewBox="0 0 6 9"
      fill="currentColor"
      css={breadcrumbArrowCss}
      {...rest}
    >
      <path d="M6 4.5L1.5 8.39711L1.5 0.602886L6 4.5Z" />
    </svg>
  ) : (
    <span css={breadcrumbSlashCss}>/</span>
  )
}
